import { useEffect, useRef } from 'react';
import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { TagsUtils } from '@frontend/api-tag';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { useFaxActions } from '../../../hooks/use-fax-actions';
import { useSelectedFaxShallowStore } from '../../../stores/selected-fax-store';

type ThreadHeaderTagsProps = {
  tags: Tag[];
  locationId: string;
  faxId: string;
  tagSelectionIsOpen: boolean;
};

export const ThreadHeaderTags = ({ tags, faxId, locationId, tagSelectionIsOpen }: ThreadHeaderTagsProps) => {
  const { untagFax } = useFaxActions();
  const { selectedFax, setSelectedFax } = useSelectedFaxShallowStore('selectedFax', 'setSelectedFax');
  const containerRef = useRef<HTMLDivElement>(null);

  const prevCountRef = useRef(tags.length);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    prevCountRef.current = tags.length;

    const calculateVisible = () => {
      requestAnimationFrame(() => {
        if (!container) return;
        const singleRowHeight = 28;

        const allChildren = Array.from(container.children).filter(
          (el) => !(el as HTMLElement).dataset.overflow
        ) as HTMLElement[];

        let count = allChildren.length;
        while (count > 0 && container.getBoundingClientRect().height > singleRowHeight) {
          count--;
        }

        if (count !== prevCountRef.current) {
          prevCountRef.current = count;
        }
      });
    };

    const observer = new ResizeObserver(calculateVisible);
    observer.observe(container);
    calculateVisible();

    return () => observer.disconnect();
  }, [tags]);

  if (tags.length === 0 && !tagSelectionIsOpen) return null;

  const sortedTags = TagsUtils.sortTagsAlphabetically(tags);
  // const hiddenCount = sortedTags.length - visibleCount;
  // TODO: update the tags to show tag stacking, 0 is correct for now
  const hiddenCount = 0;

  return (
    <div
      ref={containerRef}
      css={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'center',
      }}
    >
      {hiddenCount > 0 ? (
        <span
          data-overflow
          css={{
            fontSize: theme.fontSize(12),
            color: theme.colors.neutral50,
            whiteSpace: 'nowrap',
          }}
        >
          +{sortedTags.length}
        </span>
      ) : (
        sortedTags.map((tag) => {
          const tagId = tag.id || tag.smartTagId;
          return (
            <Chip.Tag
              key={tagId}
              color={TagsUtils.convertStringToTagColor(tag.color)}
              isRemovable
              onRemoveClick={(e: MouseEvent) => {
                e.stopPropagation();
                untagFax(locationId, faxId, [tagId]);
                if (selectedFax.faxId === faxId) {
                  setSelectedFax({ ...selectedFax, tags: selectedFax.tags?.filter((t) => t !== tagId) });
                }
              }}
              trackingIds={{
                removeButton: `${FaxPrefixes.Thread}-remove-tag`,
              }}
            >
              {tag.name}
            </Chip.Tag>
          );
        })
      )}
    </div>
  );
};
